import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PimTemplateProperties } from 'gung-list';
import {
  ProductTreeTableViewComponent,
  PriceService,
  GungFlowService,
  AuthService,
  MetadataService,
  LocationConfigService,
  Product,
  Customer,
  SelectedCustomerService,
  ColumnSortSelectionService,
  GungAnonymousConfigService,
  GridViewProductCard
} from 'gung-standard';
import { first, forkJoin, of } from 'rxjs';
import { environment } from './../../../environments/environment';
import { OttoOlsePimTemplateProperties } from '../otto-olsen-concept-detail-product-list-view/otto-olsen-concept-detail-product-list-view.component';

@Component({
  selector: 'otto-olsen-product-tree-table-view',
  templateUrl: './otto-olsen-product-tree-table-view.component.html',
  styleUrls: ['./otto-olsen-product-tree-table-view.component.scss']
})
export class OttoOlsenProductTreeTableViewComponent extends ProductTreeTableViewComponent implements OnDestroy {
  selectedCustomer: Customer;

  public Number = Number;
  public requestPriceEmail = environment.requestPriceEmail;

  firstLoad = true;

  constructor(
    protected priceService: PriceService,
    protected gungFlowService: GungFlowService,
    protected authService: AuthService,
    protected metadataService: MetadataService,
    protected locationConfigService: LocationConfigService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected columnSortSelectionService: ColumnSortSelectionService,
    private selectedCustomerService: SelectedCustomerService,
    protected gungAnonymousConfigService: GungAnonymousConfigService
  ) {
    super(priceService, gungFlowService, authService, metadataService, locationConfigService, router, route, gungAnonymousConfigService);
    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(first())
      .subscribe(customer => (this.selectedCustomer = customer));
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.columnSortSelectionService.setSelectedColumnSort(null);
    this.clearSortColumns();
  }

  sortColumn(headerKey: PimTemplateProperties) {
    const ascSort = !headerKey.sort || headerKey.sort === 'asc' ? -1 : 1;
    this.clearSortColumns();
    headerKey.sort = ascSort === 1 ? 'asc' : 'desc';
    this.columnSortSelectionService.setSelectedColumnSort(headerKey);
  }

  clearSortColumns() {
    // clear all other columns sorts
    for (const c of this.dynamicColumns) {
      c.sort = undefined;
    }
  }


  protected subscribeToProducts() {
    if (!this.oldFlow || !this.currentFlow || this.oldFlow.id !== this.currentFlow.id) {
      // remove cached data;
      this.keyedMapData = {};
      this.oldFlow = this.currentFlow;
    }
    const newIds = this.data.map(p => p.id).filter(id => !Object.keys(this.keyedMapData).includes(id));
    if (newIds.length === 0) {
      this.readMappedDataFromCache();
      return;
    }

    const subscription = forkJoin([
      of(this.data),
      this.priceService.getCurrentCustomerPrices(newIds).pipe(first()),
      this.gungFlowService.getSelectedFlow().pipe(first())
    ])
      .pipe(first())
      .subscribe(([productData, prices, flow]) => {
        let avs = [];
        if (this.isAnonymous) {
          this.includeAvailability = this.gungAnonymousConfigService.showAnonymousAvailabilities && (flow.useAvailabilities || flow.requireAvailability);
        } else {
          this.includeAvailability = flow.useAvailabilities || flow.requireAvailability;
        }
        if (this.includeAvailability) {
          avs = newIds.map(id => this.getProductAvailability(this.data.find(product => product.id === id)));
        }

        this.keyedMapData = {
          ...this.keyedMapData,
          ...newIds.reduce((acc, curr) => {
            const product = productData.filter(p => p && p.id === curr)[0];

            if (!product) {
              this.findError = true;
              this.errorMessage += 'No product found. ';
              throw new Error('No product found');
            }

            const price = prices.filter(p => p && p.productId === curr)[0];
            if (!price) {
              this.findError = true;
              this.errorMessage += 'No price found.';
              throw new Error('No price found');
            }

            const av = avs.filter(p => p && p.productId === curr)[0];
            if (!av && this.includeAvailability) {
              this.findError = true;
              this.errorMessage += 'No availability found. ';
              throw new Error('No availability found');
            }
            const item: GridViewProductCard = this.mapItem(curr, product, price, av);
            return {
              ...acc,
              [curr]: item
            };
          }, {})
        };
        this.readMappedDataFromCache();
        if (this.firstLoad) {
          this.firstLoad = false;
          this.sortDefault();
        }
      });
    this.subscriptions.push(subscription);
  }

  protected sortDefault(): void {
    for (const col of (this.dynamicColumns as OttoOlsePimTemplateProperties[])) {
      if (col.defaultSortingOrder && col.isDefaultSort) {
        const ascSort = !col.defaultSortingOrder || col.defaultSortingOrder === 'asc' ? -1 : 1;
        this.clearSortColumns();
        col.sort = ascSort === 1 ? 'asc' : 'desc';
        this.columnSortSelectionService.setSelectedColumnSort(col);
        break;
      }
    }
  }
}
